import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Package, AlertCircle, IndianRupee, CheckCircle2, Truck, FileText } from 'lucide-react';
import axiosInstance from "../../../src/api/axiosInstance";
import Authenticated from "../../layout/Authenticated";

const NewPurchaseReturn = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        supplierid: '',
        returndate: new Date().toISOString().split('T')[0],
        returnreason: 'OTHER',
        notes: '',
        originalpurchaseid: '',
        is_direct_refund: false, // New
        refund_amount: '',       // New
        refund_method: 'CASH',   // New
        refund_date: new Date().toISOString().split('T')[0] // New
    });

    const [suppliers, setSuppliers] = useState([]);
    const [purchases, setPurchases] = useState([]); // List of purchases for selected supplier
    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [returnItems, setReturnItems] = useState([]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('success');

    // Loading states
    const [loadingSuppliers, setLoadingSuppliers] = useState(true);
    const [loadingPurchases, setLoadingPurchases] = useState(false);
    const [loadingPurchaseDetails, setLoadingPurchaseDetails] = useState(false);

    const returnReasons = [
        { value: 'DAMAGED', label: 'Damaged' },
        { value: 'EXPIRED', label: 'Expired' },
        { value: 'WRONG_ITEM', label: 'Wrong Item' },
        { value: 'QUALITY_ISSUE', label: 'Quality Issue' },
        { value: 'EXCESS_STOCK', label: 'Excess Stock' },
        { value: 'OTHER', label: 'Other' }
    ];

    // Fetch Suppliers
    useEffect(() => {
        fetchSuppliers();
    }, []);

    // Fetch Purchases when supplier changes
    useEffect(() => {
        if (formData.supplierid) {
            fetchPurchases(formData.supplierid);
        } else {
            setPurchases([]);
            setReturnItems([]);
        }
    }, [formData.supplierid]);

    const calculateTotal = () => {
        return returnItems.reduce((total, item) => {
            if (item.returnquantity > 0) {
                const price = parseFloat(item.originalPrice) || 0;
                return total + (item.returnquantity * price);
            }
            return total;
        }, 0);
    };

    // Auto-update refund amount when total changes if direct refund is enabled
    useEffect(() => {
        if (formData.is_direct_refund) {
            const total = calculateTotal();
            setFormData(prev => {
                const newTotal = total.toFixed(2);
                if (prev.refund_amount !== newTotal) {
                    return { ...prev, refund_amount: newTotal };
                }
                return prev;
            });
        }
    }, [returnItems, formData.is_direct_refund]);

    const fetchSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await axiosInstance.get('/supplier');
            const data = response.data;
            let suppliersArray = Array.isArray(data) ? data : (data.data || data.suppliers || []);
            setSuppliers(suppliersArray);
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            showAlertMessage('Failed to load suppliers', 'error');
        } finally {
            setLoadingSuppliers(false);
        }
    };

    const fetchPurchases = async (supplierId) => {
        setLoadingPurchases(true);
        try {
            // Using existing purchases endpoint with filtering
            const response = await axiosInstance.get(`/purchases?supplier_id=${supplierId}&limit=100`);
            const data = response.data;
            let purchasesArray = Array.isArray(data.purchases) ? data.purchases : [];
            setPurchases(purchasesArray);
        } catch (error) {
            console.error('Error fetching purchases:', error);
            showAlertMessage('Failed to load purchase invoices', 'error');
        } finally {
            setLoadingPurchases(false);
        }
    };

    const fetchPurchaseDetails = async (purchaseId) => {
        if (!purchaseId) {
            setReturnItems([]);
            return;
        }

        setLoadingPurchaseDetails(true);
        try {
            const response = await axiosInstance.get(`/purchases/${purchaseId}`);
            const purchase = response.data.purchase;

            if (purchase && purchase.purchase_items) {
                // Map purchase items to return items structure
                const items = purchase.purchase_items.map(item => ({
                    purchaseItemId: item.id,
                    medicineid: item.medicine_id,
                    medicineName: item.medicine?.name || 'Unknown',
                    batchnumber: item.batch_number,
                    expirydate: item.expiry_date,
                    returnquantity: 0, // Default return quantity is 0
                    returnreason: 'OTHER',
                    availableQuantity: item.remaining_quantity || 0, // Using remaining_quantity from DB
                    originalPurchaseItem: item,
                    originalPrice: item.price_per_package
                }));
                // Filter out items with 0 remaining quantity? Maybe show them as disabled.
                // Let's keep them but disable input if 0.
                setReturnItems(items);
            }
        } catch (error) {
            console.error('Error fetching purchase details:', error);
            showAlertMessage('Failed to load invoice details', 'error');
        } finally {
            setLoadingPurchaseDetails(false);
        }
    };

    const showAlertMessage = (message, type = 'success') => {
        setAlertMessage(message);
        setAlertType(type);
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 5000);
    };

    const handleSupplierChange = (supplierId) => {
        const supplier = suppliers.find(s => s.id.toString() === supplierId);
        setSelectedSupplier(supplier || null);
        setFormData(prev => ({
            ...prev,
            supplierid: supplierId,
            originalpurchaseid: '' // Reset selected invoice
        }));
        setReturnItems([]); // Clear items
    };

    const handlePurchaseChange = (purchaseId) => {
        setFormData(prev => ({ ...prev, originalpurchaseid: purchaseId }));
        fetchPurchaseDetails(purchaseId);
    };

    const updateReturnItem = (index, field, value) => {
        setReturnItems(prev => prev.map((item, i) => {
            if (i === index) {
                return { ...item, [field]: value };
            }
            return item;
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.supplierid) newErrors.supplier = 'Supplier is required';
        if (!formData.returndate) newErrors.returndate = 'Return date is required';
        if (!formData.originalpurchaseid) newErrors.originalpurchaseid = 'Invoice is required';

        const returnableItems = returnItems.filter(item => item.returnquantity > 0);
        if (returnableItems.length === 0) {
            newErrors.items = 'Please enter return quantity for at least one item';
        }

        returnItems.forEach((item, index) => {
            if (item.returnquantity > 0) {
                if (item.returnquantity > item.availableQuantity) {
                    newErrors[`item_${index}_quantity`] = `Max available: ${item.availableQuantity}`;
                }
            }
        });

        // Validation for Direct Refund
        if (formData.is_direct_refund) {
            if (!formData.refund_amount || parseFloat(formData.refund_amount) <= 0) {
                newErrors.refund_amount = 'Refund amount is required and must be greater than 0';
            }
            if (!formData.refund_date) {
                newErrors.refund_date = 'Refund date is required';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);

        try {
            // Only send items with return quantity > 0
            const itemsToReturn = returnItems
                .filter(item => item.returnquantity > 0)
                .map(item => ({
                    medicineid: parseInt(item.medicineid),
                    batchnumber: item.batchnumber,
                    expirydate: item.expirydate,
                    returnquantity: parseInt(item.returnquantity),
                    returnreason: item.returnreason,
                    originalpurchaseitemid: item.purchaseItemId // Backend expects 'originalpurchaseitemid' inside items too?
                }));

            const submitData = {
                supplierid: parseInt(formData.supplierid),
                returndate: formData.returndate,
                returnreason: formData.returnreason, // Main return reason
                notes: formData.notes,
                originalpurchaseid: parseInt(formData.originalpurchaseid),
                items: itemsToReturn,
                // Direct Refund Data
                return_status: formData.is_direct_refund ? 'REFUNDED' : 'PENDING',
                refund_amount: formData.is_direct_refund ? parseFloat(formData.refund_amount) : 0,
                refund_method: formData.is_direct_refund ? formData.refund_method : null,
                refund_date: formData.is_direct_refund ? formData.refund_date : null
            };

            await axiosInstance.post('/purchase-returns', submitData);
            showAlertMessage('Purchase return created successfully!', 'success');

            setTimeout(() => {
                navigate('/purchase-return');
            }, 2000);
        } catch (error) {
            console.error('Error creating purchase return:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Failed to create return';
            showAlertMessage(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Authenticated>
            <div className="min-h-screen bg-gray-50 py-6 px-2 sm:px-4 md:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    {/* Alert */}
                    {showAlert && (
                        <div className={`fixed top-4 right-4 z-50 p-4 rounded-xl shadow-lg ${alertType === 'success'
                            ? 'bg-green-500 border border-green-400'
                            : 'bg-red-500 border border-red-400'
                            } text-white max-w-md`}>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    {alertType === 'success' ? (
                                        <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle className="h-5 w-5 flex-shrink-0" />
                                    )}
                                    <span>{alertMessage}</span>
                                </div>
                                <button
                                    onClick={() => setShowAlert(false)}
                                    className="text-white hover:text-gray-200 ml-4 text-xl font-bold flex-shrink-0"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={() => navigate('/purchase-return')}
                                    className="p-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                    <ArrowLeft size={20} />
                                </button>
                                <div className="bg-gradient-to-br from-teal-500 to-teal-600 p-3 rounded-xl shadow-md">
                                    <Package className="h-6 w-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">New Purchase Return</h1>
                                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Return items from a specific invoice</p>
                                </div>
                            </div>
                            <div className="text-left sm:text-right">
                                <div className="text-xs text-gray-500 font-medium">Return Date</div>
                                <div className="text-sm sm:text-base font-bold text-gray-900">
                                    {new Date().toLocaleDateString('en-IN', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                            <div className="bg-gradient-to-r from-teal-500 to-teal-600 p-5 sm:p-6 flex items-center gap-3">
                                <div className="bg-white bg-opacity-20 p-2.5 rounded-lg">
                                    <Truck className="h-5 w-5 text-white" />
                                </div>
                                <h2 className="text-lg sm:text-xl font-bold text-white">Purchase Return Details</h2>
                            </div>

                            <div className="p-6 sm:p-8 space-y-6">
                                {/* Top Controls: Supplier & Invoice */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Supplier Selection */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Supplier <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.supplierid}
                                            onChange={(e) => handleSupplierChange(e.target.value)}
                                            disabled={loadingSuppliers}
                                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium transition-colors"
                                        >
                                            <option value="">{loadingSuppliers ? 'Loading...' : 'Select Supplier'}</option>
                                            {suppliers.map((supplier) => (
                                                <option key={supplier.id} value={supplier.id}>
                                                    {supplier.name}
                                                </option>
                                            ))}
                                        </select>
                                        {errors.supplier && <p className="text-red-500 text-xs mt-2">{errors.supplier}</p>}
                                    </div>

                                    {/* Invoice Selection */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Invoice Number <span className="text-red-500">*</span>
                                        </label>
                                        <select
                                            value={formData.originalpurchaseid}
                                            onChange={(e) => handlePurchaseChange(e.target.value)}
                                            disabled={!formData.supplierid || loadingPurchases}
                                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium transition-colors disabled:bg-gray-100"
                                        >
                                            <option value="">
                                                {loadingPurchases ? 'Loading Invoices...' : (purchases.length === 0 ? 'No Invoices Found' : 'Select Invoice')}
                                            </option>
                                            {purchases.map((purchase) => (
                                                <option key={purchase.id} value={purchase.id}>
                                                    #{purchase.invoice_number} ({new Date(purchase.purchase_date).toLocaleDateString()})
                                                </option>
                                            ))}
                                        </select>
                                        {errors.originalpurchaseid && <p className="text-red-500 text-xs mt-2">{errors.originalpurchaseid}</p>}
                                    </div>

                                    {/* Return Date */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Return Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.returndate}
                                            onChange={(e) => setFormData(prev => ({ ...prev, returndate: e.target.value }))}
                                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Primary Reason</label>
                                        <select
                                            value={formData.returnreason}
                                            onChange={(e) => setFormData(prev => ({ ...prev, returnreason: e.target.value }))}
                                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium transition-colors"
                                        >
                                            {returnReasons.map((reason) => (
                                                <option key={reason.value} value={reason.value}>
                                                    {reason.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                                        <input
                                            value={formData.notes}
                                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                            placeholder="Optional notes..."
                                            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm font-medium"
                                        />
                                    </div>
                                </div>

                                {/* Items Table */}
                                {formData.originalpurchaseid && (
                                    <div className="border-t-2 border-gray-200 pt-6 mt-6">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="p-2 bg-teal-100 rounded-lg">
                                                <FileText className="h-5 w-5 text-teal-600" />
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900">Items in Invoice</h3>
                                        </div>

                                        {loadingPurchaseDetails ? (
                                            <p className="text-center p-4 text-gray-500 flex items-center justify-center gap-2">
                                                <div className="animate-spin h-5 w-5 border-2 border-teal-500 border-t-transparent rounded-full"></div>
                                                Loading items...
                                            </p>
                                        ) : returnItems.length === 0 ? (
                                            <p className="text-center p-4 text-gray-500">No active items found in this invoice.</p>
                                        ) : (
                                            <div className="overflow-x-auto rounded-xl border border-gray-200">
                                                <table className="w-full text-sm text-left">
                                                    <thead className="bg-gray-50 text-gray-700 font-semibold uppercase text-xs">
                                                        <tr>
                                                            <th className="px-4 py-3">Medicine</th>
                                                            <th className="px-4 py-3">Batch</th>
                                                            <th className="px-4 py-3">Expiry</th>
                                                            <th className="px-4 py-3 text-right">Price</th>
                                                            <th className="px-4 py-3 text-center">Available</th>
                                                            <th className="px-4 py-3 w-32">Return Qty</th>
                                                            <th className="px-4 py-3 w-40">Reason</th>
                                                            <th className="px-4 py-3 text-right">Total</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-100">
                                                        {returnItems.map((item, index) => (
                                                            <tr key={`${item.purchaseItemId}_${index}`} className={`hover:bg-gray-50 transition-colors ${item.availableQuantity === 0 ? 'opacity-50 bg-gray-50' : ''}`}>
                                                                <td className="px-4 py-3 font-medium text-gray-900">
                                                                    {item.medicineName}
                                                                </td>
                                                                <td className="px-4 py-3 text-gray-600">{item.batchnumber}</td>
                                                                <td className="px-4 py-3 text-gray-600">{new Date(item.expirydate).toLocaleDateString()}</td>
                                                                <td className="px-4 py-3 text-right">₹{parseFloat(item.originalPrice).toFixed(2)}</td>
                                                                <td className="px-4 py-3 text-center">
                                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${item.availableQuantity > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                        {item.availableQuantity}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        max={item.availableQuantity}
                                                                        disabled={item.availableQuantity === 0}
                                                                        value={item.returnquantity}
                                                                        onChange={(e) => updateReturnItem(index, 'returnquantity', e.target.value)}
                                                                        className={`w-full px-2 py-1.5 border rounded-lg focus:ring-2 focus:ring-teal-500 text-center ${errors[`item_${index}_quantity`] ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}
                                                                    />
                                                                    {errors[`item_${index}_quantity`] && (
                                                                        <div className="text-[10px] text-red-500 mt-1 leading-tight">{errors[`item_${index}_quantity`]}</div>
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-3">
                                                                    <select
                                                                        value={item.returnreason}
                                                                        disabled={item.availableQuantity === 0 || item.returnquantity == 0}
                                                                        onChange={(e) => updateReturnItem(index, 'returnreason', e.target.value)}
                                                                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs"
                                                                    >
                                                                        {returnReasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                                                    </select>
                                                                </td>
                                                                <td className="px-4 py-3 text-right font-bold text-teal-600">
                                                                    {(item.returnquantity > 0) ? `₹${(item.returnquantity * parseFloat(item.originalPrice)).toFixed(2)}` : '-'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {/* Total Summary */}
                                        {returnItems.length > 0 && (
                                            <div className="mt-6 flex justify-end">
                                                <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 w-full max-w-sm">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-gray-600 text-sm">Total items to return:</span>
                                                        <span className="font-bold text-gray-900">{returnItems.filter(i => i.returnquantity > 0).length}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center border-t border-teal-200 pt-2">
                                                        <span className="text-teal-800 font-bold">Total Return Value:</span>
                                                        <span className="text-2xl font-bold text-teal-600">₹{calculateTotal().toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {errors.items && (
                                            <div className="mt-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg flex items-center gap-2 border border-red-200">
                                                <AlertCircle size={16} />
                                                {errors.items}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Direct Refund Section */}
                                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mt-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                            Direct Refund (Status: REFUNDED)
                                        </h2>
                                        <label className="flex items-center cursor-pointer relative">
                                            <input
                                                type="checkbox"
                                                checked={formData.is_direct_refund}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        is_direct_refund: isChecked,
                                                        refund_amount: isChecked ? calculateTotal().toFixed(2) : ''
                                                    }));
                                                }}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                                            <span className="ml-3 text-sm font-medium text-gray-900">Enable Direct Refund</span>
                                        </label>
                                    </div>

                                    {formData.is_direct_refund && (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate-fadeIn">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Refund Method <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    value={formData.refund_method}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, refund_method: e.target.value }))}
                                                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                                >
                                                    <option value="CASH">Cash</option>
                                                    <option value="ONLINE">Bank/Online</option>
                                                    <option value="CREDIT_NOTE">Credit Note</option>
                                                    <option value="ADJUSTMENT">Adjustment</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Refund Amount <span className="text-red-500">*</span>
                                                </label>
                                                <div className="relative">
                                                    <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={formData.refund_amount}
                                                        onChange={(e) => setFormData(prev => ({ ...prev, refund_amount: e.target.value }))}
                                                        className="w-full pl-9 px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm font-bold text-teal-700"
                                                    />
                                                </div>
                                                {errors.refund_amount && <p className="text-red-500 text-xs mt-1">{errors.refund_amount}</p>}
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Refund Date <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    value={formData.refund_date}
                                                    onChange={(e) => setFormData(prev => ({ ...prev, refund_date: e.target.value }))}
                                                    className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                                                />
                                                {errors.refund_date && <p className="text-red-500 text-xs mt-1">{errors.refund_date}</p>}
                                            </div>
                                            <div className="md:col-span-3 bg-blue-50 p-3 rounded-lg border border-blue-200 text-sm text-blue-800 flex items-center gap-2">
                                                <AlertCircle className="h-4 w-4" />
                                                Stock will be deducted from inventory immediately upon creation.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                                <button
                                    type="button"
                                    onClick={() => navigate('/purchase-return')}
                                    className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting || loadingPurchaseDetails}
                                    className="px-6 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-sm hover:shadow transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <Save size={18} />
                                    {isSubmitting ? 'Processing...' : 'Create Purchase Return'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </Authenticated>
    );
};

export default NewPurchaseReturn;